import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid2 as Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Divider,
  Menu,
  ListItemButton,
  Tooltip,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Skeleton,
  FilterList,
  Checkbox,
  FormControlLabel,
  ButtonGroup,
  SpeedDial,
  SpeedDialAction,
  Fab,
} from "@mui/material";
import {
  PhotoCamera,
  CloudUpload,
  Delete,
  Download,
  Visibility,
  Close,
  NavigateNext,
  NavigateBefore,
  Edit,
  Share,
  Filter,
  Sort,
  ViewModule,
  ViewList,
  Timeline as TimelineIcon,
  Category,
  LocationOn,
  Image,
  Info,
  Security,
  Engineering,
  Assignment,
  CheckCircle,
  Error,
  Schedule,
  Add,
  CloudDownload,
  Print,
  ZoomIn,
  ZoomOut,
  RotateRight,
  Fullscreen,
  Comment,
  Label,
  Search,
  ArrowBack,
} from "@mui/icons-material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import FileDropzone from "../components/FileDropzone";

const PpPropertyPhotoGalleryPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [property, setProperty] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState("grid"); // grid, list, timeline
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedSource, setSelectedSource] = useState("all");
  const [sortBy, setSortBy] = useState("date_desc");
  const [searchQuery, setSearchQuery] = useState("");

  // Photo viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [viewerZoom, setViewerZoom] = useState(100);
  const [viewerRotation, setViewerRotation] = useState(0);

  // Upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [newPhotos, setNewPhotos] = useState([]);
  const [uploadCategory, setUploadCategory] = useState("");
  const [photoNotes, setPhotoNotes] = useState("");
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  // Edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);

  // Selection state
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);

  // Mock property data
  const mockProperty = {
    id: propertyId || "PROP2024001",
    address: "123 Oak Street, Jacksonville, FL 32207",
    permitNumber: "PKG2024001",
    type: "residential",
    status: "active",
    contractor: {
      name: "ABC Construction",
      license: "CBC123456",
    },
  };

  // Mock photo data with different categories and sources
  const mockPhotos = [
    {
      id: "PHOTO001",
      filename: "property_exterior_front.jpg",
      url: "https://via.placeholder.com/800x600/0066cc/ffffff?text=Front+Exterior",
      thumbnail:
        "https://via.placeholder.com/300x200/0066cc/ffffff?text=Front+Exterior",
      category: "property_context",
      source: "inspector", // inspector, contractor, reviewer
      uploadedBy: "John Inspector",
      uploadDate: "2024-01-15T10:30:00Z",
      notes: "Front exterior view showing main entrance and driveway access",
      tags: ["exterior", "front", "access"],
      size: "2.1 MB",
      dimensions: "3024x4032",
      location: { lat: 30.3322, lng: -81.6557 },
      metadata: {
        camera: "iPhone 13 Pro",
        settings: "f/1.5, 1/120s, ISO 125",
      },
    },
    {
      id: "PHOTO002",
      filename: "foundation_inspection.jpg",
      url: "https://via.placeholder.com/800x600/cc6600/ffffff?text=Foundation+Inspection",
      thumbnail:
        "https://via.placeholder.com/300x200/cc6600/ffffff?text=Foundation+Inspection",
      category: "inspection",
      source: "inspector",
      uploadedBy: "John Inspector",
      uploadDate: "2024-01-16T14:45:00Z",
      notes:
        "Foundation inspection showing proper concrete placement and rebar positioning",
      tags: ["foundation", "concrete", "rebar", "passed"],
      size: "1.8 MB",
      dimensions: "2400x3200",
      inspectionResult: "passed",
      codeReference: "IBC 1805.4.2",
    },
    {
      id: "PHOTO003",
      filename: "site_conditions_aerial.jpg",
      url: "https://via.placeholder.com/800x600/00cc66/ffffff?text=Aerial+Site+View",
      thumbnail:
        "https://via.placeholder.com/300x200/00cc66/ffffff?text=Aerial+Site+View",
      category: "site_conditions",
      source: "contractor",
      uploadedBy: "ABC Construction",
      uploadDate: "2024-01-12T09:15:00Z",
      notes: "Aerial view showing site layout and neighboring properties",
      tags: ["aerial", "site", "neighbors", "layout"],
      size: "3.2 MB",
      dimensions: "4000x3000",
    },
    {
      id: "PHOTO004",
      filename: "code_compliance_electrical.jpg",
      url: "https://via.placeholder.com/800x600/cc0066/ffffff?text=Electrical+Compliance",
      thumbnail:
        "https://via.placeholder.com/300x200/cc0066/ffffff?text=Electrical+Compliance",
      category: "compliance",
      source: "inspector",
      uploadedBy: "Jane Electrical Inspector",
      uploadDate: "2024-01-18T11:20:00Z",
      notes: "Electrical panel installation meets NFPA 70 requirements",
      tags: ["electrical", "panel", "compliance", "NFPA"],
      size: "1.5 MB",
      dimensions: "2048x1536",
      inspectionResult: "passed",
      codeReference: "NFPA 70 - Article 408",
    },
    {
      id: "PHOTO005",
      filename: "framing_issue_beam.jpg",
      url: "https://via.placeholder.com/800x600/cc3300/ffffff?text=Framing+Issue",
      thumbnail:
        "https://via.placeholder.com/300x200/cc3300/ffffff?text=Framing+Issue",
      category: "issues",
      source: "inspector",
      uploadedBy: "John Inspector",
      uploadDate: "2024-01-17T16:00:00Z",
      notes: "Beam connection requires additional support per IRC R502.6",
      tags: ["framing", "beam", "support", "failed", "correction"],
      size: "2.4 MB",
      dimensions: "3200x2400",
      inspectionResult: "failed",
      codeReference: "IRC R502.6",
      issueStatus: "corrected",
    },
  ];

  const photoCategories = [
    { value: "all", label: "All Photos", count: 0, color: "default" },
    {
      value: "property_context",
      label: "Property Context",
      count: 0,
      color: "info",
    },
    {
      value: "site_conditions",
      label: "Site Conditions",
      count: 0,
      color: "success",
    },
    { value: "inspection", label: "Inspections", count: 0, color: "primary" },
    {
      value: "compliance",
      label: "Code Compliance",
      count: 0,
      color: "secondary",
    },
    {
      value: "issues",
      label: "Issues & Corrections",
      count: 0,
      color: "error",
    },
    { value: "progress", label: "Progress", count: 0, color: "warning" },
  ];

  const photoSources = [
    { value: "all", label: "All Sources", count: 0 },
    { value: "inspector", label: "Inspector", count: 0 },
    { value: "contractor", label: "Contractor", count: 0 },
    { value: "reviewer", label: "Reviewer", count: 0 },
  ];

  useEffect(() => {
    setProperty(mockProperty);
    setPhotos(mockPhotos);
  }, [propertyId]);

  useEffect(() => {
    let filtered = [...photos];

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (photo) => photo.category === selectedCategory
      );
    }

    // Filter by source
    if (selectedSource !== "all") {
      filtered = filtered.filter((photo) => photo.source === selectedSource);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (photo) =>
          photo.filename.toLowerCase().includes(query) ||
          photo.notes.toLowerCase().includes(query) ||
          photo.tags.some((tag) => tag.toLowerCase().includes(query)) ||
          photo.uploadedBy.toLowerCase().includes(query)
      );
    }

    // Sort photos
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "date_desc":
          return new Date(b.uploadDate) - new Date(a.uploadDate);
        case "date_asc":
          return new Date(a.uploadDate) - new Date(b.uploadDate);
        case "name_asc":
          return a.filename.localeCompare(b.filename);
        case "name_desc":
          return b.filename.localeCompare(a.filename);
        case "size_desc":
          return parseFloat(b.size) - parseFloat(a.size);
        case "size_asc":
          return parseFloat(a.size) - parseFloat(b.size);
        default:
          return 0;
      }
    });

    setFilteredPhotos(filtered);
  }, [photos, selectedCategory, selectedSource, searchQuery, sortBy]);

  // Update category counts
  useEffect(() => {
    const categoriesWithCounts = photoCategories.map((cat) => ({
      ...cat,
      count:
        cat.value === "all"
          ? photos.length
          : photos.filter((p) => p.category === cat.value).length,
    }));
    // You would update state here if categories were stateful
  }, [photos]);

  const handlePhotoClick = (photo) => {
    const index = filteredPhotos.findIndex((p) => p.id === photo.id);
    setCurrentPhoto(photo);
    setCurrentPhotoIndex(index);
    setViewerOpen(true);
    setViewerZoom(100);
    setViewerRotation(0);
  };

  const handleNextPhoto = () => {
    if (currentPhotoIndex < filteredPhotos.length - 1) {
      const nextIndex = currentPhotoIndex + 1;
      setCurrentPhotoIndex(nextIndex);
      setCurrentPhoto(filteredPhotos[nextIndex]);
      setViewerZoom(100);
      setViewerRotation(0);
    }
  };

  const handlePrevPhoto = () => {
    if (currentPhotoIndex > 0) {
      const prevIndex = currentPhotoIndex - 1;
      setCurrentPhotoIndex(prevIndex);
      setCurrentPhoto(filteredPhotos[prevIndex]);
      setViewerZoom(100);
      setViewerRotation(0);
    }
  };

  const handlePhotoSelection = (newPhotos) => {
    setNewPhotos([...newPhotos]);
  };

  const handlePhotoRemove = (photoId) => {
    setNewPhotos(newPhotos.filter((p) => p.id !== photoId));
  };

  const handleUploadPhotos = async () => {
    if (newPhotos.length === 0) return;

    try {
      setUploadingPhotos(true);

      // Mock API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Add to photos list with proper metadata
      const uploadedPhotos = newPhotos.map((photo) => ({
        ...photo,
        id: `PHOTO_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        category: uploadCategory,
        source: "reviewer", // Current user type
        uploadedBy: "Current User",
        uploadDate: new Date().toISOString(),
        notes: photoNotes,
        tags: [],
        url: `https://via.placeholder.com/800x600/0066cc/ffffff?text=${encodeURIComponent(photo.name)}`,
        thumbnail: `https://via.placeholder.com/300x200/0066cc/ffffff?text=${encodeURIComponent(photo.name)}`,
      }));

      setPhotos([...photos, ...uploadedPhotos]);
      setNewPhotos([]);
      setPhotoNotes("");
      setUploadCategory("");
      setUploadDialogOpen(false);
    } catch (error) {
      console.error("Failed to upload photos:", error);
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleBulkDownload = () => {
    if (selectedPhotos.length === 0) return;

    selectedPhotos.forEach((photoId, index) => {
      const photo = photos.find((p) => p.id === photoId);
      if (photo) {
        setTimeout(() => {
          const link = document.createElement("a");
          link.href = photo.url;
          link.download = photo.filename;
          link.click();
        }, index * 500); // Stagger downloads
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedPhotos.length === 0) return;

    if (window.confirm(`Delete ${selectedPhotos.length} selected photos?`)) {
      setPhotos(photos.filter((p) => !selectedPhotos.includes(p.id)));
      setSelectedPhotos([]);
      setSelectionMode(false);
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos((prev) =>
      prev.includes(photoId)
        ? prev.filter((id) => id !== photoId)
        : [...prev, photoId]
    );
  };

  const selectAllPhotos = () => {
    setSelectedPhotos(filteredPhotos.map((p) => p.id));
  };

  const clearSelection = () => {
    setSelectedPhotos([]);
    setSelectionMode(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "property_context":
        return <LocationOn />;
      case "site_conditions":
        return <Image />;
      case "inspection":
        return <Assignment />;
      case "compliance":
        return <Security />;
      case "issues":
        return <Error />;
      case "progress":
        return <Schedule />;
      default:
        return <PhotoCamera />;
    }
  };

  const getSourceAvatar = (source) => {
    const colors = {
      inspector: "#1976d2",
      contractor: "#388e3c",
      reviewer: "#7b1fa2",
    };

    return (
      <Avatar
        sx={{ bgcolor: colors[source] || "#757575", width: 24, height: 24 }}
      >
        {source.charAt(0).toUpperCase()}
      </Avatar>
    );
  };

  if (!property) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography>Loading property gallery...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/pp/workbench/PRS001")}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography color="primary" variant="h4">
            Photo Gallery
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {property.address} • {filteredPhotos.length} photos
          </Typography>
        </Box>

        {/* Header Actions */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Photos
          </Button>

          {selectionMode && (
            <>
              <Typography variant="body2" color="text.secondary">
                {selectedPhotos.length} selected
              </Typography>
              <ButtonGroup size="small">
                <Button
                  onClick={handleBulkDownload}
                  disabled={selectedPhotos.length === 0}
                >
                  <Download />
                </Button>
                <Button
                  onClick={handleBulkDelete}
                  disabled={selectedPhotos.length === 0}
                >
                  <Delete />
                </Button>
                <Button onClick={clearSelection}>
                  <Close />
                </Button>
              </ButtonGroup>
            </>
          )}

          <IconButton onClick={() => setSelectionMode(!selectionMode)}>
            <Checkbox />
          </IconButton>
        </Box>
      </Box>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search photos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Category"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {photoCategories.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {cat.value !== "all" && getCategoryIcon(cat.value)}
                        {cat.label} ({cat.count})
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Source</InputLabel>
                <Select
                  value={selectedSource}
                  label="Source"
                  onChange={(e) => setSelectedSource(e.target.value)}
                >
                  {photoSources.map((source) => (
                    <MenuItem key={source.value} value={source.value}>
                      {source.label} (
                      {
                        photos.filter(
                          (p) =>
                            source.value === "all" || p.source === source.value
                        ).length
                      }
                      )
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="date_desc">Newest First</MenuItem>
                  <MenuItem value="date_asc">Oldest First</MenuItem>
                  <MenuItem value="name_asc">Name A-Z</MenuItem>
                  <MenuItem value="name_desc">Name Z-A</MenuItem>
                  <MenuItem value="size_desc">Size Large-Small</MenuItem>
                  <MenuItem value="size_asc">Size Small-Large</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <ButtonGroup size="small">
                <Button
                  variant={viewMode === "grid" ? "contained" : "outlined"}
                  onClick={() => setViewMode("grid")}
                >
                  <ViewModule />
                </Button>
                <Button
                  variant={viewMode === "list" ? "contained" : "outlined"}
                  onClick={() => setViewMode("list")}
                >
                  <ViewList />
                </Button>
                <Button
                  variant={viewMode === "timeline" ? "contained" : "outlined"}
                  onClick={() => setViewMode("timeline")}
                >
                  <TimelineIcon />
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Photo Display */}
      {filteredPhotos.length === 0 ? (
        <Alert severity="info" sx={{ mt: 3 }}>
          No photos found matching your filters. Try adjusting your search
          criteria or upload some photos to get started.
        </Alert>
      ) : (
        <>
          {viewMode === "grid" && (
            <ImageList cols={4} rowHeight={200} gap={16}>
              {filteredPhotos.map((photo) => (
                <ImageListItem key={photo.id} sx={{ position: "relative" }}>
                  {selectionMode && (
                    <Checkbox
                      checked={selectedPhotos.includes(photo.id)}
                      onChange={() => togglePhotoSelection(photo.id)}
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        zIndex: 2,
                        bgcolor: "rgba(255, 255, 255, 0.8)",
                        "&:hover": { bgcolor: "rgba(255, 255, 255, 0.9)" },
                      }}
                    />
                  )}

                  <img
                    src={photo.thumbnail}
                    alt={photo.filename}
                    loading="lazy"
                    style={{
                      cursor: "pointer",
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onClick={() => !selectionMode && handlePhotoClick(photo)}
                  />

                  <ImageListItemBar
                    title={photo.filename}
                    subtitle={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {getSourceAvatar(photo.source)}
                        <Typography variant="caption">
                          {formatDate(photo.uploadDate)}
                        </Typography>
                      </Box>
                    }
                    actionIcon={
                      <Box>
                        <Chip
                          size="small"
                          label={photo.category.replace("_", " ")}
                          color={
                            photoCategories.find(
                              (c) => c.value === photo.category
                            )?.color || "default"
                          }
                          sx={{ mr: 1 }}
                        />
                        <IconButton
                          sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePhotoClick(photo);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Box>
                    }
                  />
                </ImageListItem>
              ))}
            </ImageList>
          )}

          {viewMode === "list" && (
            <List>
              {filteredPhotos.map((photo) => (
                <ListItem key={photo.id}>
                  {selectionMode && (
                    <Checkbox
                      checked={selectedPhotos.includes(photo.id)}
                      onChange={() => togglePhotoSelection(photo.id)}
                      sx={{ mr: 2 }}
                    />
                  )}

                  <ListItemIcon>
                    <Avatar
                      src={photo.thumbnail}
                      variant="rounded"
                      sx={{ width: 60, height: 60 }}
                    >
                      <PhotoCamera />
                    </Avatar>
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {photo.filename}
                        <Chip
                          size="small"
                          label={photo.category.replace("_", " ")}
                          color={
                            photoCategories.find(
                              (c) => c.value === photo.category
                            )?.color || "default"
                          }
                        />
                        {photo.inspectionResult && (
                          <Chip
                            size="small"
                            label={photo.inspectionResult}
                            color={
                              photo.inspectionResult === "passed"
                                ? "success"
                                : "error"
                            }
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {photo.notes}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          By {photo.uploadedBy} • {formatDate(photo.uploadDate)}{" "}
                          • {photo.size}
                        </Typography>
                      </Box>
                    }
                  />

                  <ListItemSecondaryAction>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton onClick={() => handlePhotoClick(photo)}>
                        <Visibility />
                      </IconButton>
                      <IconButton>
                        <Download />
                      </IconButton>
                      <IconButton>
                        <Edit />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          {viewMode === "timeline" && (
            <Timeline>
              {filteredPhotos.map((photo, index) => (
                <TimelineItem key={photo.id}>
                  <TimelineOppositeContent sx={{ m: "auto 0" }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(photo.uploadDate)}
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                    >
                      {photo.uploadedBy}
                    </Typography>
                  </TimelineOppositeContent>

                  <TimelineSeparator>
                    <TimelineDot
                      color={
                        photoCategories.find((c) => c.value === photo.category)
                          ?.color || "grey"
                      }
                    >
                      {getCategoryIcon(photo.category)}
                    </TimelineDot>
                    {index < filteredPhotos.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>

                  <TimelineContent sx={{ py: "12px", px: 2 }}>
                    <Card sx={{ maxWidth: 400 }}>
                      <CardContent>
                        <img
                          src={photo.thumbnail}
                          alt={photo.filename}
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                            borderRadius: 4,
                            cursor: "pointer",
                          }}
                          onClick={() => handlePhotoClick(photo)}
                        />
                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                          {photo.filename}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {photo.notes}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            mt: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          {photo.tags.map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          )}
        </>
      )}

      {/* Photo Viewer Dialog */}
      <Dialog
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: "90vh" } }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6">{currentPhoto?.filename}</Typography>
              <Chip
                size="small"
                label={currentPhoto?.category?.replace("_", " ")}
                color={
                  photoCategories.find(
                    (c) => c.value === currentPhoto?.category
                  )?.color || "default"
                }
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {currentPhotoIndex + 1} of {filteredPhotos.length}
              </Typography>
              <ButtonGroup size="small">
                <IconButton
                  onClick={() => setViewerZoom(Math.max(25, viewerZoom - 25))}
                >
                  <ZoomOut />
                </IconButton>
                <Button variant="outlined" sx={{ minWidth: 60 }}>
                  {viewerZoom}%
                </Button>
                <IconButton
                  onClick={() => setViewerZoom(Math.min(400, viewerZoom + 25))}
                >
                  <ZoomIn />
                </IconButton>
                <IconButton
                  onClick={() => setViewerRotation((viewerRotation + 90) % 360)}
                >
                  <RotateRight />
                </IconButton>
              </ButtonGroup>
              <IconButton onClick={() => setViewerOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, position: "relative", overflow: "hidden" }}>
          {/* Navigation buttons */}
          <IconButton
            onClick={handlePrevPhoto}
            disabled={currentPhotoIndex === 0}
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
              bgcolor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              "&:hover": { bgcolor: "rgba(0, 0, 0, 0.7)" },
            }}
          >
            <NavigateBefore />
          </IconButton>

          <IconButton
            onClick={handleNextPhoto}
            disabled={currentPhotoIndex === filteredPhotos.length - 1}
            sx={{
              position: "absolute",
              right: 16,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
              bgcolor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              "&:hover": { bgcolor: "rgba(0, 0, 0, 0.7)" },
            }}
          >
            <NavigateNext />
          </IconButton>

          {/* Photo display */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              bgcolor: "#f5f5f5",
              overflow: "auto",
            }}
          >
            {currentPhoto && (
              <img
                src={currentPhoto.url}
                alt={currentPhoto.filename}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  transform: `scale(${viewerZoom / 100}) rotate(${viewerRotation}deg)`,
                  transition: "transform 0.2s ease",
                }}
              />
            )}
          </Box>
        </DialogContent>

        <DialogContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2">Photo Details</Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Uploaded by:</strong> {currentPhoto?.uploadedBy}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Date:</strong>{" "}
                {currentPhoto && formatDate(currentPhoto.uploadDate)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Size:</strong> {currentPhoto?.size} •{" "}
                {currentPhoto?.dimensions}
              </Typography>
              {currentPhoto?.codeReference && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Code Reference:</strong> {currentPhoto.codeReference}
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2">Notes</Typography>
              <Typography variant="body2" color="text.secondary">
                {currentPhoto?.notes || "No notes provided"}
              </Typography>

              {currentPhoto?.tags && currentPhoto.tags.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2">Tags</Typography>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {currentPhoto.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button startIcon={<Download />}>Download</Button>
          <Button startIcon={<Edit />}>Edit</Button>
          <Button startIcon={<Share />}>Share</Button>
          <Button onClick={() => setViewerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload New Photos</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Upload photos to document property conditions, inspection results,
            or reference materials.
          </Alert>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Photo Category</InputLabel>
            <Select
              value={uploadCategory}
              label="Photo Category"
              onChange={(e) => setUploadCategory(e.target.value)}
            >
              {photoCategories.slice(1).map(
                (
                  cat // Skip "all" option
                ) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {getCategoryIcon(cat.value)}
                      {cat.label}
                    </Box>
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>

          <FileDropzone
            onFilesSelected={handlePhotoSelection}
            onFileRemove={handlePhotoRemove}
            existingFiles={newPhotos}
            acceptedFileTypes={[".jpg", ".jpeg", ".png", ".pdf"]}
            maxFiles={20}
            maxFileSize={10 * 1024 * 1024} // 10MB
            generateFileName={() =>
              `${property.id}_${uploadCategory}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            }
            disabled={uploadingPhotos}
          />

          {newPhotos.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Photo Notes"
                value={photoNotes}
                onChange={(e) => setPhotoNotes(e.target.value)}
                placeholder="Add notes about these photos..."
                disabled={uploadingPhotos}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUploadDialogOpen(false)}
            disabled={uploadingPhotos}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadPhotos}
            disabled={
              newPhotos.length === 0 || !uploadCategory || uploadingPhotos
            }
            startIcon={uploadingPhotos ? null : <CloudUpload />}
          >
            {uploadingPhotos
              ? "Uploading..."
              : `Upload ${newPhotos.length} Photo${newPhotos.length !== 1 ? "s" : ""}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Selection mode controls */}
      {selectionMode && selectedPhotos.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            bottom: 20,
            right: 20,
            zIndex: 1300,
          }}
        >
          <ButtonGroup orientation="vertical">
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleBulkDownload}
            >
              Download ({selectedPhotos.length})
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={handleBulkDelete}
            >
              Delete ({selectedPhotos.length})
            </Button>
          </ButtonGroup>
        </Box>
      )}
    </Box>
  );
};

export default PpPropertyPhotoGalleryPage;
